import {
  useEffect,
  useRef,
  type KeyboardEvent,
  type ClipboardEvent,
} from "react";
import { cn } from "@/lib/cn";

interface EditableTextNodeProps {
  value: string;
  onCommit: (newValue: string) => void;
  editable?: boolean;
  multiline?: boolean;
  className?: string;
  as?: "span" | "div";
}

/**
 * In-place editable text. Renders a contenteditable element seeded with
 * `value`. On blur or commit-key, calls `onCommit(newText)`. On Escape,
 * reverts to `value` without firing onCommit. Strips formatting on paste
 * — only plain text comes in.
 *
 * Used for per-text-node editing of agent-produced text frames. Does NOT
 * support structural editing — the caller wraps individual leaf text
 * nodes only.
 */
export function EditableTextNode({
  value,
  onCommit,
  editable = true,
  multiline = false,
  className,
  as: Tag = "span",
}: EditableTextNodeProps) {
  const ref = useRef<HTMLElement | null>(null);

  // Sync the DOM text content when the prop changes from outside (e.g.
  // after a successful commit re-renders the parent with new content).
  useEffect(() => {
    const el = ref.current;
    if (el && el.textContent !== value && document.activeElement !== el) {
      el.textContent = value;
    }
  }, [value]);

  const commit = () => {
    const el = ref.current;
    if (!el) return;
    const newText = el.textContent ?? "";
    if (newText !== value) onCommit(newText);
  };

  const revert = () => {
    const el = ref.current;
    if (el) el.textContent = value;
    el?.blur();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      revert();
      return;
    }
    if (e.key === "Enter") {
      const isCommit = multiline ? e.metaKey || e.ctrlKey : !e.shiftKey;
      if (isCommit) {
        e.preventDefault();
        commit();
        ref.current?.blur();
      }
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLElement>) => {
    e.preventDefault();
    const text = e.clipboardData?.getData("text/plain") ?? "";
    // Insert at the current selection as plain text.
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(document.createTextNode(text));
      range.collapse(false);
    }
  };

  return (
    <Tag
      ref={ref as React.RefObject<HTMLSpanElement & HTMLDivElement>}
      contentEditable={editable}
      suppressContentEditableWarning
      onBlur={editable ? commit : undefined}
      onKeyDown={editable ? onKeyDown : undefined}
      onPaste={editable ? onPaste : undefined}
      className={cn(
        editable && "outline-none focus:ring-1 rounded-sm px-0.5 -mx-0.5",
        className,
      )}
      spellCheck
      data-testid="editable-text-node"
    >
      {value}
    </Tag>
  );
}
